import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Calendar, CheckCircle2, Clock, Hash, IndianRupee, Loader2, MapPin, Stethoscope, Ticket } from 'lucide-react';
import { IITH, SERIF, SANS } from './landing/iithBrand';
import { formatDateIST } from '../utils/date';

const API = process.env.REACT_APP_BACKEND_URL;

function Detail({ icon: Icon, label, value, mono, primary }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-3.5 py-3">
      <Icon className="h-4 w-4 mt-0.5 shrink-0" style={{ color: primary }} />
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-[0.14em]" style={{ color: IITH.gold, fontFamily: SANS }}>{label}</p>
        <p className={`mt-0.5 text-sm text-white ${mono ? 'font-mono font-semibold' : 'font-semibold'}`}>{value}</p>
      </div>
    </div>
  );
}

/** Public appointment card opened from email / QR (opaque access token). */
export default function PublicAppointmentPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/tenant/appointment/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setError('This appointment could not be found or the link is invalid.'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: IITH.ink }}>
        <Loader2 className="h-7 w-7 animate-spin" style={{ color: IITH.saffron }} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: IITH.ink }}>
        <div className="max-w-md text-center rounded-2xl border border-white/10 bg-white/5 p-8">
          <p className="text-xl text-white font-bold" style={{ fontFamily: SERIF }}>Appointment unavailable</p>
          <p className="mt-2 text-sm" style={{ color: IITH.mute }}>{error}</p>
        </div>
      </div>
    );
  }

  const brand = data.tenant?.branding || {};
  const primary = brand.primary_color || IITH.saffron;
  const logo = brand.logo_url || '/assets/iith-logo.png';
  const title = brand.display_name || data.tenant?.name || IITH.hospital;
  const fee = Number(data.suggested_fee || 0);
  const payDue = Boolean(data.payment_required) || (
    fee > 0 && !['paid', 'waived'].includes(data.payment_status || '')
  );
  const feeText = `₹${fee.toLocaleString('en-IN')}`;

  return (
    <div className="min-h-screen text-white" style={{ background: IITH.ink }} data-testid="public-appointment">
      <div className="h-1" style={{ background: `linear-gradient(90deg, ${primary}, ${IITH.gold}, ${primary})` }} />
      <header className="px-5 py-5" style={{ background: `linear-gradient(125deg, ${IITH.navy}, ${IITH.navyMid})` }}>
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <img src={logo} alt="" className="h-12 w-12 rounded-full bg-white p-1 object-contain" />
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-white/80">{IITH.institute}</p>
            <h1 className="text-xl font-bold leading-tight" style={{ fontFamily: SERIF }}>{title}</h1>
          </div>
        </div>
      </header>
      <main className="max-w-lg mx-auto px-5 py-8">
        <div className={`rounded-2xl border p-5 mb-5 flex items-start gap-3 ${
          payDue ? 'border-amber-400/40 bg-amber-500/15' : 'border-emerald-400/30 bg-emerald-500/10'
        }`}>
          <CheckCircle2 className={`h-6 w-6 flex-shrink-0 mt-0.5 ${payDue ? 'text-amber-300' : 'text-emerald-400'}`} />
          <div>
            <p className={`text-sm font-semibold ${payDue ? 'text-amber-200' : 'text-emerald-200'}`}>
              {payDue ? 'Booked — payment due at hospital' : 'Digital visit pass'}
            </p>
            <p className="text-[11px] mt-1 opacity-80" style={{ fontFamily: SANS }}>
              {payDue
                ? `Pay ${feeText} at reception. Fully confirmed only after payment and triage.`
                : 'Show this page or your email QR at reception check-in.'}
            </p>
          </div>
        </div>
        {payDue ? (
          <div className="rounded-2xl border border-amber-400/35 bg-amber-500/10 p-4 mb-4" data-testid="public-payment-notice">
            <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-amber-300">Consultation fee</p>
            <p className="text-2xl font-bold text-white mt-1">{feeText}</p>
            <p className="text-[12px] text-amber-100/90 mt-2 leading-relaxed" style={{ fontFamily: SANS }}>
              Your slot is reserved. Pay at the hospital desk (cash / UPI / card).
              Your appointment is fully confirmed only after payment at reception.
            </p>
          </div>
        ) : null}
        <div className="rounded-2xl border border-[#C4A35A]/30 bg-[#0C2744] p-5 space-y-2.5">
          <Detail icon={Hash} label="Patient number" value={data.patient_number} mono primary={primary} />
          <Detail icon={Ticket} label="Appointment number" value={data.appointment_number} mono primary={primary} />
          <Detail icon={Stethoscope} label="Doctor" value={data.doctor_name} primary={primary} />
          <Detail icon={Calendar} label="Date" value={data.date ? formatDateIST(data.date) : ''} primary={primary} />
          <Detail icon={Clock} label="Time" value={data.slot} primary={primary} />
          <Detail icon={Ticket} label="Queue token" value={data.queue_no ? `#${data.queue_no}` : 'Issued after triage'} primary={primary} />
          {payDue ? <Detail icon={IndianRupee} label="Fee due at hospital" value={feeText} primary={primary} /> : null}
          <Detail icon={MapPin} label="Patient" value={data.name} primary={primary} />
        </div>
        <p className="text-center text-[11px] mt-6" style={{ color: IITH.mute, fontFamily: SANS }}>
          {IITH.campus} · Arrive 10 minutes early with photo ID
        </p>
      </main>
    </div>
  );
}
